import React, { useState, useMemo } from 'react';
import { Plus, Check, Phone, CalendarDays, ListTodo as ListTodoIcon, Mail } from 'lucide-react';
import { Avatar, Badge, Modal, fmtDate } from '../components/ui.jsx';
import { priorityColor, userById } from '../data.js';
import { relatedName } from '../adapters.js';

const typeIcon = { Call: Phone, Meeting: CalendarDays, Task: ListTodoIcon, Email: Mail };
const statusTone = { Open: 'warn', Done: 'pass' };

function AddTaskModal({ onClose, onCreate, accounts, partners, team }) {
  const firstAccount = accounts[0];
  const [form, setForm] = useState({
    title: '', type: 'Task', relatedType: firstAccount ? 'Account' : 'Partner', relatedId: firstAccount?.id || partners[0]?.id || '',
    due: '', owner: team[0]?.id || '', priority: 'Normal',
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setRelated = (e) => {
    const [relatedType, relatedId] = e.target.value.split('::');
    setForm({ ...form, relatedType, relatedId });
  };
  return (
    <Modal title="New task" subtitle="Log a task, call, meeting or email." onClose={onClose}
      actions={<><button className="btn btn-ghost" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={() => { onCreate(form); onClose(); }}>Create</button></>}>
      <label>Title<input placeholder="e.g. Send renewal quote" value={form.title} onChange={set('title')} autoFocus /></label>
      <div className="modal-form-row">
        <label>Type<select value={form.type} onChange={set('type')}><option>Task</option><option>Call</option><option>Meeting</option><option>Email</option></select></label>
        <label>Priority<select value={form.priority} onChange={set('priority')}><option>Critical</option><option>High</option><option>Normal</option><option>Low</option></select></label>
      </div>
      <label>Related to<select value={`${form.relatedType}::${form.relatedId}`} onChange={setRelated}>
        <optgroup label="Accounts">{accounts.map((a) => <option key={a.id} value={`Account::${a.id}`}>{a.name}</option>)}</optgroup>
        <optgroup label="Partners">{partners.map((p) => <option key={p.id} value={`Partner::${p.id}`}>{p.name}</option>)}</optgroup>
      </select></label>
      <div className="modal-form-row">
        <label>Due date<input type="date" value={form.due} onChange={set('due')} /></label>
        <label>Owner<select value={form.owner} onChange={set('owner')}>{team.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select></label>
      </div>
    </Modal>
  );
}

export default function Tasks({ tasks = [], accounts = [], partners = [], team = [], onCreateTask, onToggleDone }) {
  const [filter, setFilter] = useState('Open');
  const [modal, setModal] = useState(false);

  const filtered = useMemo(() => tasks.filter((t) => filter === 'All' || t.status === filter), [tasks, filter]);

  return (
    <>
      <div className="page-head">
        <div className="page-head-left">
          <div className="page-title">Tasks & Activities</div>
          <div className="page-subtitle">{tasks.filter((t) => t.status !== 'Done').length} open items across the team.</div>
        </div>
        <div className="page-actions"><button className="btn btn-primary" onClick={() => setModal(true)}><Plus size={15} /> New task</button></div>
      </div>

      <div className="tabs" style={{ marginBottom: 16 }}>
        {['Open', 'Done', 'All'].map((f) => (
          <span key={f} className={`tab-btn ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>{f}</span>
        ))}
      </div>

      <div className="card">
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th></th><th>Task</th><th>Type</th><th>Related to</th><th>Due date</th><th>Owner</th><th>Priority</th><th>Status</th></tr></thead>
            <tbody>
              {filtered.map((t) => {
                const Icon = typeIcon[t.type] || ListTodoIcon;
                return (
                  <tr key={t.id}>
                    <td>
                      <button className="btn-icon" style={t.status === 'Done' ? { color: 'var(--pass)', borderColor: 'var(--pass)' } : undefined} onClick={() => onToggleDone(t.id)}>
                        <Check size={13} />
                      </button>
                    </td>
                    <td className="td-name" style={t.status === 'Done' ? { textDecoration: 'line-through', color: 'var(--text-muted)' } : undefined}>{t.title}</td>
                    <td><span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Icon size={13} />{t.type}</span></td>
                    <td className="td-muted">{relatedName(t, accounts, partners)}</td>
                    <td className="td-muted">{fmtDate(t.due)}</td>
                    <td><Avatar name={userById(team, t.owner)?.name} color={userById(team, t.owner)?.color} size="sm" /></td>
                    <td><Badge tone={priorityColor[t.priority]}>{t.priority}</Badge></td>
                    <td><Badge tone={statusTone[t.status]}>{t.status}</Badge></td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={8} className="table-empty">Nothing here — you're caught up.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {modal && <AddTaskModal accounts={accounts} partners={partners} team={team} onClose={() => setModal(false)} onCreate={onCreateTask} />}
    </>
  );
}
